import { IProduct, ICMSApiResponse, BadgeImagesBySku, BadgeImageWithSku, EventEditionDetails, BadgeImage } from "../../../../common/types";
import OfflineStorage from "../../../../common/offlineStorage/OfflineStorage";
import CMSHelper from "../../../../common/helpers/cms/CMSHelper";
import { NETWORK_FAILURE_ERROR } from "../../../../common/constants";
import store from "../../../../redux/store";

export const useCmsImages = () => {
  const getLogosFromCms = async (products: IProduct[], eventEdition) => {
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails) {
      return;
    }

    const logoUrls = await Promise.all(
      products.map(async product => {
        return fetchCmsImageUrl(product.sku, "logo", eventEdition, cmsEventDetails, {
          useSharedLogo: product.rx_shared_logo_usage === 1
        });
      })
    );

    const uniqueLogoUrls = Array.from(new Set(logoUrls.filter(Boolean)));
    const requests = uniqueLogoUrls.map(logoUrl => toDataURL(logoUrl, undefined, false, true));

    await Promise.all(requests);
  };

  function groupImagesBySku(products: IProduct[], badgeImagesWithSku: BadgeImageWithSku[]) {
    let badgeImagesBySku: BadgeImagesBySku[] = [];

    for (const product of products) {
      const imagesWithSku = badgeImagesWithSku.filter(badge => badge.sku === product.sku);
      let imagesBySku: BadgeImage[] = [];
      for (const badge of imagesWithSku) {
        imagesBySku = [...imagesBySku, { name: badge.name, base64Image: badge.base64Image }];
      }
      badgeImagesBySku = [...badgeImagesBySku, { sku: product.sku, images: imagesBySku }];
    }
    return badgeImagesBySku;
  }

  const getBadgeImagesFromCms = async (products: IProduct[], eventEdition) => {
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    if (!cmsEventDetails) {
      return;
    }
    const imageNames = ["image1", "image2", "image3"];
    const eventProducts = products.filter(pr => pr.rx_event_edition_code === eventEdition.gbsCode);

    const requests = eventProducts.flatMap(product =>
      imageNames.map(async imageName => {
        const imageUrl = await fetchCmsImageUrl(product.sku, imageName, eventEdition, cmsEventDetails);
        return toDataURL(imageUrl, undefined, false, true);
      })
    );

    await Promise.all(requests);
  };

  const getReceiptLogoUrl = async (locale: string) => {
    return CMSHelper.getReceiptLogoUrl(locale);
  };

  const getFreyaLogoUrl = (logoUrl, baseUrl) => {
    const NUMBER_OF_DIRECTORIES = 5;
    const splitLogoUrl = (logoUrl || "").split("/");
    const rmIndex = splitLogoUrl.indexOf("content");
    return `${baseUrl}/${splitLogoUrl
      .slice(rmIndex, rmIndex + NUMBER_OF_DIRECTORIES)
      .join("/")}/freya/logo.jpg/_jcr_content/renditions/original./123/logo.jpg`;
  };

  const readDataFromUrl = (callback, xhr: XMLHttpRequest) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };

  const toDataURL = (
    url: string,
    callback?: (result: string) => void,
    convertToDataUrl = false,
    ignoreNotFound = true
  ): Promise<string> => {
    const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          if (ignoreNotFound && xhr.status === 404) {
            console.warn(`Image not found (404): ${url}`);
            resolve("");
            return;
          }

          if (ignoreNotFound && isOffline()) {
            reject(new Error(NETWORK_FAILURE_ERROR));
            return;
          }

          reject(new Error(`Failed to download image from ${url}`));
          return;
        }

        if (!convertToDataUrl && !callback) {
          resolve("");
          return;
        }

        readDataFromUrl((result: string) => {
          if (callback) {
            callback(result);
          }
          resolve(result);
        }, xhr);
      };
      xhr.onerror = () => {
        if (ignoreNotFound && isOffline()) {
          reject(new Error(NETWORK_FAILURE_ERROR));
          return;
        }

        if (ignoreNotFound) {
          console.warn(`Image request failed and was skipped: ${url}`);
          resolve("");
          return;
        }
        reject(new Error(`Failed to download image from ${url}`));
      };
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    });
  };

  const fetchCmsImageUrl = async (
    productSku: string,
    imageName: string,
    eventEdition,
    badgeLocation: ICMSApiResponse | null,
    options?: { useSharedLogo?: boolean }
  ) => {
    const eventLogoUrl = badgeLocation?.siteDetails?.logo?.url || "";
    const domain = new URL(eventEdition.domain || "").origin;
    const url = options?.useSharedLogo
      ? CMSHelper.getPortalSharedLogoUrl(eventLogoUrl, domain)
      : CMSHelper.getPortalImageUrl(eventLogoUrl, domain, imageName, productSku);

    return url;
  };

  const fetchEventLogo = async (eventEdition: EventEditionDetails) => {
    if (!eventEdition.domain) {
      return;
    }
    const cmsEventDetails = store.getState().eventEdition.cmsEventDetails;
    const logoPath = cmsEventDetails?.siteDetails?.logo.url;
    const domain = new URL(eventEdition.domain || "").origin;
    const logoUrl = getFreyaLogoUrl(logoPath, domain);
    const base64Image = await toDataURL(logoUrl, undefined, true, true);
    if (!base64Image || !Boolean(["png", "jpeg"].find(format => base64Image.toLowerCase().includes(`data:image/${format};`)))) return "";
    return OfflineStorage.addEventLogoToStorage(base64Image);
  };

  const _forTestsOnly = { groupImagesBySku, fetchCmsImageUrl, toDataURL, readDataFromUrl };

  return { getLogosFromCms, getBadgeImagesFromCms, fetchEventLogo, _forTestsOnly, getReceiptLogoUrl };
};
